import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { Schema } from '~~/common/types/api';
import { setErrorNotification } from 'common/utils/helper';
import { AW_AX_CACHE_DEFAULT } from '~~/common/plugins/axios/awAxCache';
import { CATALOG_FOOTER_PAGE_TYPE_ROUTE_MAP, CONTENT_ROOT_TYPE_TO_PAGE_TYPE, RESOURCE_TYPE_TO_PAGE_TYPE } from '~~/shop/app/config/CatalogFooterPageTypeMap';
import { useUrlResourceStore } from '~~/common/stores/urlResource';
import { useCareerStore } from '~~/common/stores/career';
import { useContentsStore } from '~~/common/stores/contents.js';
import { computed, ref } from 'vue';

interface CatalogFooterData {
  sectionTitle: string;
  urls: string[];
  pageTypes: Record<string, boolean>;
}

export const useCatalogFooterStore = defineStore('catalogFooter', () => {
  const nuxtApp = useNuxtApp();

  const catalogFooterData = ref<CatalogFooterData | null>(null);

  async function getCatalogFooterData () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      catalogFooterData.value = await $api.$get<CatalogFooterData>('/catalog/footer_config', { awAxCache: AW_AX_CACHE_DEFAULT });
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }
  }

  const getSectionTitle = computed(() => {
    return catalogFooterData.value?.sectionTitle;
  });

  function isCatalogFooterSectionVisible (currentRoute: RouteLocationNormalizedLoaded | string): boolean {
    if (!catalogFooterData.value) {
      return false;
    }

    const { urls, pageTypes } = catalogFooterData.value;

    const path = typeof currentRoute === 'string' ? currentRoute : currentRoute.path;
    const rawName = typeof currentRoute === 'string' ? '' : String(currentRoute.name ?? '');
    // Strip locale suffix, e.g. "shop-basket___hu" → "shop-basket"
    const routeName = rawName.replace(/___\w+$/, '');

    // urls has priority — explicit list always wins
    if (urls.includes(path)) {
      return true;
    }

    // [...slug] catch-all: for SEO-friendly URLs (e.g. .p-, .c-, .b-.c-)
    // the page type is determined by the urlResourceStore resource type
    if (routeName === 'slug') {
      const careerStore = useCareerStore();
      if (careerStore.showCareerJobListPage) {
        return Boolean(pageTypes.career);
      }

      const urlResourceStore = useUrlResourceStore();
      const resourceType = (urlResourceStore.data as Schema<'UrlModel'>)?.resourceType;
      const pageTypeKey = RESOURCE_TYPE_TO_PAGE_TYPE[resourceType];

      if (resourceType === 'cms_content') {
        const contentsStore = useContentsStore();
        const contentRootTypes: string[] = (contentsStore.data as { contentRootTypes?: string[] })?.contentRootTypes ?? [];
        if (Object.entries(CONTENT_ROOT_TYPE_TO_PAGE_TYPE).some(([cmsPageTypeKey, triggers]) => Boolean(pageTypes[cmsPageTypeKey]) && triggers.some(t => contentRootTypes.includes(t)))) {
          return true;
        }
      }

      return pageTypeKey ? Boolean(pageTypes[pageTypeKey]) : false;
    }

    // Every other route: mapping by route name
    for (const [pageType, enabled] of Object.entries(pageTypes)) {
      if (!enabled) {
        continue;
      }
      const matchingRoutes = CATALOG_FOOTER_PAGE_TYPE_ROUTE_MAP[pageType] ?? [];
      if (matchingRoutes.includes(routeName)) {
        return true;
      }
    }

    return false;
  }


  return {
    catalogFooterData,
    getCatalogFooterData,
    getSectionTitle,
    isCatalogFooterSectionVisible,
  };
});
